import React from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, ScrollText } from "lucide-react";
import { useLang } from "@/context/LangContext";
import PrestigeMedallion from "@/components/PrestigeMedallion";

const TR = {
  sk: {
    eyebrow: "Sieň Slávy SauberEko",
    title: "Patrí sem tvoje meno?",
    subtitle: "Verejný register SauberEko® certifikovaných profesionálov a firiem. Každý certifikát značky SauberEko® je viazaný na unikátne evidenčné číslo — žiadne falošné odznaky.",
    cta: "Získaj certifikát",
    aboutBanner: "Čo je certifikát Sa.Eko.",
    aboutBannerSub: "Príbeh značky · Kódex · Profesionáli · Firmy",
  },
  en: {
    eyebrow: "SauberEko Hall of Fame",
    title: "Does your name belong here?",
    subtitle: "Public registry of SauberEko® certified professionals and companies. Every SauberEko® certificate is tied to a unique registry number — no fake badges.",
    cta: "Get certified",
    aboutBanner: "What is the Sa.Eko. certificate",
    aboutBannerSub: "Brand story · Code · Professionals · Companies",
  },
  cs: {
    eyebrow: "Síň Slávy SauberEko",
    title: "Patří sem tvé jméno?",
    subtitle: "Veřejný registr SauberEko® certifikovaných profesionálů a firem. Každý certifikát značky SauberEko® je vázán na unikátní evidenční číslo — žádné falešné odznaky.",
    cta: "Získej certifikát",
    aboutBanner: "Co je certifikát Sa.Eko.",
    aboutBannerSub: "Příběh značky · Kodex · Profesionálové · Firmy",
  },
  de: {
    eyebrow: "SauberEko Hall of Fame",
    title: "Gehört dein Name hierher?",
    subtitle: "Öffentliches Register von SauberEko®-zertifizierten Profis und Unternehmen. Jedes SauberEko®-Zertifikat ist an eine eindeutige Registernummer gebunden — keine gefälschten Abzeichen.",
    cta: "Zertifikat erhalten",
    aboutBanner: "Was ist das Sa.Eko.-Zertifikat",
    aboutBannerSub: "Markengeschichte · Kodex · Profis · Unternehmen",
  },
  pl: {
    eyebrow: "Sala Sławy SauberEko",
    title: "Czy Twoje nazwisko należy tutaj?",
    subtitle: "Publiczny rejestr profesjonalistów i firm certyfikowanych SauberEko®. Każdy certyfikat marki SauberEko® jest powiązany z unikalnym numerem ewidencyjnym — żadnych fałszywych odznak.",
    cta: "Zdobądź certyfikat",
    aboutBanner: "Czym jest certyfikat Sa.Eko.",
    aboutBannerSub: "Historia marki · Kodeks · Profesjonaliści · Firmy",
  },
  fr: {
    eyebrow: "Hall of Fame SauberEko",
    title: "Ton nom devrait figurer ici ?",
    subtitle: "Registre public des professionnels et entreprises certifiés SauberEko®. Chaque certificat SauberEko® est lié à un numéro d'enregistrement unique — pas de faux badges.",
    cta: "Obtenir un certificat",
    aboutBanner: "Qu'est-ce qu'un certificat Sa.Eko.",
    aboutBannerSub: "Histoire de la marque · Code · Professionnels · Entreprises",
  },
};

export default function HallOfFameSection() {
  const { lang } = useLang();
  const t = TR[lang] || TR.sk;

  return (
    <section
      className="relative border-t border-gold-900/40 overflow-hidden"
      data-testid="landing-hall-of-fame"
    >
      {/* Background — radial gold glow + diagonal shimmer */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.03) 25%, transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, transparent 0 80px, rgba(212,175,55,0.4) 80px 81px)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-24">
        {/* Gold banner — "What is the Sa.Eko. certificate" (public blog page).
            Placed above the Hall of Fame medallion so unregistered visitors
            immediately understand the brand & certificates before scrolling further. */}
        <div className="flex justify-center mb-10">
          <Link
            to="/co-je-sa-eko"
            data-testid="landing-about-cert-banner"
            className="group relative inline-flex items-center gap-4 px-6 py-3.5 bg-gradient-to-r from-gold-950/60 via-gold-900/50 to-gold-950/60 border border-gold-600 hover:border-gold-400 transition-all overflow-hidden shadow-[0_6px_28px_-6px_rgba(212,175,55,0.5)] hover:shadow-[0_8px_36px_-6px_rgba(212,175,55,0.75)]"
          >
            {/* Shine sweep on hover */}
            <span
              className="absolute inset-y-0 left-[-40%] w-1/2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.35) 50%, transparent 100%)",
              }}
            />
            <div className="w-9 h-9 border border-gold-500 bg-gold-500/10 flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-4 h-4 text-gold-300" strokeWidth={2} />
            </div>
            <div className="text-left">
              <div className="font-display font-bold text-sm md:text-base text-gold-100 leading-tight uppercase tracking-wide">
                {t.aboutBanner}
              </div>
              <div className="text-[10px] md:text-[11px] font-mono text-gold-500/80 uppercase tracking-widest mt-0.5">
                {t.aboutBannerSub}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gold-300 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </Link>
        </div>

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          {/* Luxurious prestige medallion */}
          <div className="flex justify-center mb-6">
            <PrestigeMedallion size={104} />
          </div>

          {/* SINGLE primary CTA right below the medallion */}
          <Link
            to="/wall-of-fame"
            data-testid="landing-hof-medallion-btn"
            className="group relative inline-flex items-center gap-3 px-7 py-3.5 bg-gradient-to-r from-gold-500 via-gold-400 to-gold-500 hover:from-gold-400 hover:via-gold-300 hover:to-gold-400 text-black font-display font-bold text-base uppercase tracking-[0.15em] transition-all hover:shadow-2xl mb-7 overflow-hidden"
            style={{
              boxShadow: "0 8px 32px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
          >
            {/* shine sweep */}
            <span
              className="absolute inset-y-0 left-[-50%] w-1/2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)",
              }}
            />
            <Trophy className="w-4 h-4" strokeWidth={2.5} />
            <span className="relative">{t.eyebrow}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>

          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            <span className="bg-gradient-to-r from-gold-200 via-gold-400 to-gold-600 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h2>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed">{t.subtitle}</p>
        </div>

        {/* Single secondary CTA — main "Sieň Slávy" button already lives under the medallion */}
        <div className="mt-12 flex justify-center">
          <a
            href="https://saubereko.eu/home-1/SauberEko/iados-o-partnerstvo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="landing-hof-cta"
            className="inline-flex items-center gap-2 px-6 py-3 border border-gold-600 text-gold-300 hover:bg-gold-900/20 font-mono uppercase tracking-wider text-xs font-bold transition-colors"
          >
            <Trophy className="w-3 h-3" /> {t.cta}
          </a>
        </div>
      </div>
    </section>
  );
}
